import { useEffect, useRef, useState, useCallback } from "react";

/**
 * WebRTC voice mesh for party rooms.
 *
 * Message contract via the shared party WebSocket:
 *   send:    {type: "voice_join", muted}
 *            {type: "voice_leave"}
 *            {type: "voice_mute", muted}
 *            {type: "voice_signal", to: <peer_id>, data: {sdp} | {ice}}
 *   recv:    {type: "voice_peers", peers: [peer_id, ...]}  // sent to joiner listing existing voice peers
 *            {type: "voice_joined", peer_id, username}     // a new peer joined
 *            {type: "voice_left", peer_id}
 *            {type: "voice_signal", from, data}
 *
 * Design: each peer offers to peers already in the room; a peer with a lower peer_id
 * initiates the offer to avoid glare. New joiners always offer to existing peers.
 */
const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export function usePartyVoice(ws, myPeerId) {
  const [enabled, setEnabled] = useState(false);
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [speakingPeers, setSpeakingPeers] = useState({}); // peer_id -> bool
  const localStreamRef = useRef(null);
  const peersRef = useRef({}); // peer_id -> RTCPeerConnection
  const audioElsRef = useRef({}); // peer_id -> HTMLAudioElement
  const analysersRef = useRef({}); // peer_id -> AnalyserNode
  const rafRef = useRef(null);

  const send = useCallback((msg) => {
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
  }, [ws]);

  const cleanupPeer = useCallback((pid) => {
    const pc = peersRef.current[pid];
    if (pc) { try { pc.close(); } catch { /* ignore */ } }
    delete peersRef.current[pid];
    const a = audioElsRef.current[pid];
    if (a) { try { a.srcObject = null; a.remove(); } catch { /* ignore */ } }
    delete audioElsRef.current[pid];
    delete analysersRef.current[pid];
    setSpeakingPeers((s) => { const n = { ...s }; delete n[pid]; return n; });
  }, []);

  const createPeer = useCallback((remoteId, isInitiator) => {
    if (peersRef.current[remoteId]) return peersRef.current[remoteId];
    const pc = new RTCPeerConnection(RTC_CONFIG);
    peersRef.current[remoteId] = pc;

    // Add local audio track(s)
    const stream = localStreamRef.current;
    if (stream) stream.getAudioTracks().forEach((t) => pc.addTrack(t, stream));

    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "voice_signal", to: remoteId, data: { ice: e.candidate } });
    };
    pc.ontrack = (e) => {
      const remoteStream = e.streams[0];
      let a = audioElsRef.current[remoteId];
      if (!a) {
        a = document.createElement("audio");
        a.autoplay = true;
        a.playsInline = true;
        document.body.appendChild(a);
        audioElsRef.current[remoteId] = a;
      }
      a.srcObject = remoteStream;
      a.muted = deafened;

      // Set up analyser for speaking indicator (best-effort; ignore in unsupported browsers)
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioContext();
        const src = ctx.createMediaStreamSource(remoteStream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        analysersRef.current[remoteId] = analyser;
      } catch { /* ignore */ }
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState)) {
        cleanupPeer(remoteId);
      }
    };

    if (isInitiator) {
      (async () => {
        try {
          const offer = await pc.createOffer({ offerToReceiveAudio: true });
          await pc.setLocalDescription(offer);
          send({ type: "voice_signal", to: remoteId, data: { sdp: pc.localDescription } });
        } catch { /* ignore */ }
      })();
    }
    return pc;
  }, [send, deafened, cleanupPeer]);

  // Handle incoming voice signal messages on the shared ws
  useEffect(() => {
    if (!ws) return;
    const handler = async (e) => {
      let m; try { m = JSON.parse(e.data); } catch { return; }
      if (m.type === "voice_peers") {
        // I just joined — initiate offers to all listed peers
        (m.peers || []).forEach((pid) => createPeer(pid, true));
      } else if (m.type === "voice_joined") {
        // Someone else joined — don't create yet; they'll offer to us
      } else if (m.type === "voice_left") {
        cleanupPeer(m.peer_id);
      } else if (m.type === "voice_signal") {
        const from = m.from;
        const data = m.data || {};
        let pc = peersRef.current[from];
        if (!pc) pc = createPeer(from, false);
        try {
          if (data.sdp) {
            await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
            if (data.sdp.type === "offer") {
              const ans = await pc.createAnswer();
              await pc.setLocalDescription(ans);
              send({ type: "voice_signal", to: from, data: { sdp: pc.localDescription } });
            }
          } else if (data.ice) {
            try { await pc.addIceCandidate(data.ice); } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [ws, createPeer, cleanupPeer, send]);

  // Speaking detection loop
  useEffect(() => {
    if (!enabled) return;
    const buf = new Uint8Array(256);
    const tick = () => {
      const next = {};
      for (const [pid, an] of Object.entries(analysersRef.current)) {
        try {
          an.getByteFrequencyData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i];
          next[pid] = sum / buf.length > 12; // threshold
        } catch { /* ignore */ }
      }
      setSpeakingPeers(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [enabled]);

  const join = useCallback(async () => {
    if (enabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      stream.getAudioTracks().forEach((t) => (t.enabled = !muted));
      setEnabled(true);
      send({ type: "voice_join", muted });
    } catch (e) {
      alert("Microphone access denied or unavailable.");
    }
  }, [enabled, muted, send]);

  const leave = useCallback(() => {
    send({ type: "voice_leave" });
    Object.keys(peersRef.current).forEach(cleanupPeer);
    const s = localStreamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    setEnabled(false);
    setSpeakingPeers({});
  }, [send, cleanupPeer]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    const s = localStreamRef.current;
    if (s) s.getAudioTracks().forEach((t) => (t.enabled = !next));
    send({ type: "voice_mute", muted: next });
  }, [muted, send]);

  const toggleDeafen = useCallback(() => {
    const next = !deafened;
    setDeafened(next);
    Object.values(audioElsRef.current).forEach((a) => (a.muted = next));
  }, [deafened]);

  // Cleanup on unmount
  useEffect(() => () => {
    Object.keys(peersRef.current).forEach(cleanupPeer);
    const s = localStreamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
  }, [cleanupPeer]);

  return { enabled, muted, deafened, speakingPeers, join, leave, toggleMute, toggleDeafen };
}
